import { ReactElement } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'

import { CancelIcon } from 'components/Icons/CancelIcon'
import FormLayout from 'components/v2/FormLayout'
import { IDaysToDepartureSettingsFormProps } from 'features/InventoryRules/components/DaysToDepartureSettingsForm/types'
import { InputNumber } from 'components/InputNumber'
import { Select } from 'components/v2/Select'
import { ErrorMessage } from 'components/v2/ErrorMessage'

const Wrapper = styled.div`
  display: grid;
  gap: 16px;
`

const ItemWrapper = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  white-space: nowrap;

  select {
    min-width: 110px;
  }
`

const messages = defineMessages({
  selectCabinStrategy: {
    id: 'inventoryRules.form.daysToDeparture.form.selectCabinStrategy',
    defaultMessage: 'Strategy'
  },
  cabinStrategyName: {
    id: 'inventoryRules.form.daysToDeparture.form.cabinStrategyName',
    defaultMessage: 'Strategy {strategyNumber}'
  }
})

export const DaysToDepartureSettingsForm = ({
  daysToDepartureSettings,
  setDaysToDepartureSettings,
  cabinStrategies,
  touched,
  errors,
  onBlur,
  prefixId
}: IDaysToDepartureSettingsFormProps): ReactElement => {
  const intl = useIntl()

  return (
    <Wrapper>
      {daysToDepartureSettings.map((daysToDepartureSetting, index) => {
        const deleteDaysToDepartureSetting = (): void => {
          setDaysToDepartureSettings(daysToDepartureSettings.filter((_, settingIndex) => settingIndex !== index))
        }

        const handleChange = (change: Partial<{ start: number; end: number; cabinStrategyIndex: number }>): void => {
          setDaysToDepartureSettings(
            daysToDepartureSettings.map((daysToDepartureSetting, settingIndex) => {
              if (index === settingIndex) {
                return {
                  ...daysToDepartureSetting,
                  ...change
                }
              }
              return daysToDepartureSetting
            })
          )
        }

        const error =
          (touched?.[index]?.start && errors?.[index]?.start) ||
          (touched?.[index]?.end && errors?.[index]?.end) ||
          (touched?.[index]?.cabinStrategyIndex && errors?.[index]?.cabinStrategyIndex)
        return (
          <div key={index}>
            <ItemWrapper>
              <div>
                <InputNumber
                  id={`${prefixId}[${index}].start`}
                  value={daysToDepartureSetting.start}
                  onChange={(start: string): void => handleChange({ start: parseInt(start) })}
                  onBlur={onBlur}
                  small
                />
              </div>
              <div>
                <FormattedMessage id="inventoryRules.form.daysToDeparture.form.to" defaultMessage="to" />
              </div>
              <div>
                <InputNumber
                  id={`${prefixId}[${index}].end`}
                  value={daysToDepartureSetting.end}
                  onChange={(end: string): void => handleChange({ end: parseInt(end) })}
                  onBlur={onBlur}
                  small
                />
              </div>
              <div>
                <FormattedMessage
                  id="inventoryRules.form.daysToDeparture.form.daysByCabinStrategy"
                  defaultMessage="days, then"
                />
              </div>
              <div>
                <Select
                  id={`${prefixId}[${index}].cabinStrategyIndex`}
                  onChange={({ target: { value } }): void => handleChange({ cabinStrategyIndex: parseInt(value) })}
                  value={daysToDepartureSetting.cabinStrategyIndex}
                  small
                >
                  <option value="" disabled>
                    {intl.formatMessage(messages.selectCabinStrategy)}
                  </option>
                  {cabinStrategies.map((cabinStrategy, index): ReactElement | null => {
                    if (cabinStrategy.isDefault) return null

                    return (
                      <option value={index} key={index}>
                        {intl.formatMessage(messages.cabinStrategyName, {
                          strategyNumber: index + 1
                        })}
                      </option>
                    )
                  })}
                </Select>
              </div>
              <div>
                <FormLayout.DeleteItemAction onClick={deleteDaysToDepartureSetting}>
                  <CancelIcon />
                </FormLayout.DeleteItemAction>
              </div>
            </ItemWrapper>
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </div>
        )
      })}
    </Wrapper>
  )
}
